/**
 * Single source of truth for the "get the orderYaa app" poster/QR flow (orderyaa.com/get-app).
 *
 * The QR on the poster points at /get-app; the visitor is auto-sent to the right store for their
 * device (server-side 302 in production, client-side redirect as a fallback). Both the SSR server
 * (server.ts) and the /get-app splash component import from here so they never drift.
 *
 * Android points at the public Play listing. iOS is still UNPUBLISHED: set APP_STORE_URL to
 * https://apps.apple.com/app/id<APPLE_APP_ID> when that listing goes live, or `null` to show
 * "Coming soon" instead of redirecting to a dead link.
 */

/**
 * iOS destination. There is no App Store listing yet, so iPhone/iPad visitors are sent to the web
 * storefront `/ordering` (relative — resolves to the current host, i.e. orderyaa.com/ordering in
 * production, localhost in dev). When the iOS app ships, set this to the real listing
 * (https://apps.apple.com/app/id<APPLE_APP_ID>) and the page automatically switches from
 * "Order on the web" back to "Download on the App Store".
 */
export const APP_STORE_URL: string | null = '/ordering';

/**
 * Play Store URL for the Android app — the PUBLIC listing. Anyone can install from this, unlike the
 * closed internal-test link it replaced, which only worked for testers on the allow-list.
 */
export const PLAY_STORE_URL: string | null =
  'https://play.google.com/store/apps/details?id=com.orderyaa.app';

export type MobilePlatform = 'ios' | 'android' | 'other';

/**
 * Classify a raw user-agent string for app-store routing. Pure and SSR-safe — no browser globals —
 * so the SSR server can call it on the request User-Agent. Note: iPadOS 13+ reports as "Macintosh",
 * so a server-side classification of an iPad is "other"; the client component refines that using
 * navigator.maxTouchPoints (see get-app.ts).
 */
export function detectMobilePlatform(userAgent: string | undefined | null): MobilePlatform {
  const ua = (userAgent || '').toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  if (/android/.test(ua)) return 'android';
  return 'other';
}

/** The store URL for a platform, or null if that platform isn't published yet. */
export function storeUrlFor(platform: MobilePlatform): string | null {
  if (platform === 'ios') return APP_STORE_URL;
  if (platform === 'android') return PLAY_STORE_URL;
  return null;
}
